import planning from './planning/en'

export default {
  nav: {
    dashboard: 'Dashboard',
    projects: 'Projects',
    maps: 'Maps',
    data: 'Data',
    analytics: 'Analytics',
    settings: 'Settings',
    profile: 'Profile',
    team: 'Team',
    billing: 'Billing',
    apiKeys: 'API Keys',
  },
  dashboard: {
    title: 'Dashboard',
    overview: 'Overview',
    recentActivity: 'Recent Activity',
    quickStats: 'Quick Stats',
    totalProjects: 'Total Projects',
    totalMaps: 'Total Maps',
    totalUsers: 'Total Users',
    storageUsed: 'Storage Used',
  },
  projects: {
    title: 'Projects',
    createProject: 'Create Project',
    myProjects: 'My Projects',
    sharedProjects: 'Shared Projects',
    archivedProjects: 'Archived Projects',
    projectName: 'Project Name',
    description: 'Description',
    createdAt: 'Created',
    updatedAt: 'Last Updated',
    owner: 'Owner',
    members: 'Members',
    noProjects: 'No projects found',
    deleteProject: 'Delete Project',
    archiveProject: 'Archive Project',
    restoreProject: 'Restore Project',
  },
  maps: {
    title: 'Maps',
    createMap: 'Create Map',
    myMaps: 'My Maps',
    sharedMaps: 'Shared Maps',
    mapName: 'Map Name',
    mapType: 'Map Type',
    layers: 'Layers',
    addLayer: 'Add Layer',
    removeLayer: 'Remove Layer',
    exportMap: 'Export Map',
    shareMap: 'Share Map',
  },
  data: {
    title: 'Data',
    uploadData: 'Upload Data',
    importData: 'Import Data',
    exportData: 'Export Data',
    dataSource: 'Data Source',
    dataSources: 'Data Sources',
    dataset: 'Dataset',
    datasets: 'Datasets',
    records: 'Records',
    columns: 'Columns',
    preview: 'Preview',
  },
  analytics: {
    title: 'Analytics',
    reports: 'Reports',
    createReport: 'Create Report',
    viewReport: 'View Report',
    exportReport: 'Export Report',
    spatialAnalysis: 'Spatial Analysis',
    statistics: 'Statistics',
    trends: 'Trends',
    insights: 'Insights',
  },
  settings: {
    title: 'Settings',
    general: 'General',
    account: 'Account',
    security: 'Security',
    notifications: 'Notifications',
    preferences: 'Preferences',
    language: 'Language',
    timezone: 'Timezone',
    appearance: 'Appearance',
    theme: 'Theme',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
    systemDefault: 'System Default',
  },
  team: {
    title: 'Team',
    inviteMember: 'Invite Member',
    members: 'Members',
    pending: 'Pending Invitations',
    role: 'Role',
    admin: 'Admin',
    member: 'Member',
    viewer: 'Viewer',
    removeMember: 'Remove Member',
    changeRole: 'Change Role',
  },
  billing: {
    title: 'Billing & Subscription',
    subtitle: 'Manage your subscription, credit balance, and seats.',
    loading: 'Loading subscription…',
    nav: 'Billing & Subscription',
    currentPlan: 'Current Plan',
    usage: 'Usage',
    invoices: 'Invoices',
    paymentMethod: 'Payment Method',
    updatePayment: 'Update Payment Method',
    upgradePlan: 'Upgrade Plan',
    downgradePlan: 'Downgrade Plan',
    cancelSubscription: 'Cancel Subscription',
    subscription: {
      title: 'Subscription',
      cadence: 'Billing cadence',
      seats: 'Seats',
      seatsBreakdown: '{{admin}} admin · {{publisher}} publisher · {{viewer}} viewer',
      periodEnd: 'Current period ends',
      trialEnd: 'Trial ends',
      cancelAtPeriodEnd: 'This subscription is scheduled to cancel at the end of the current period.',
      endedOn: 'Your subscription ended on {{date}}.',
    },
    cancel: {
      button: 'Cancel subscription',
      confirmTitle: 'Cancel subscription?',
      confirmBody: 'Your subscription stays active until {{date}}, then cancels. You keep full access until then.',
      confirmCta: 'Cancel subscription',
      keep: 'Keep subscription',
      reasonLabel: 'Why are you cancelling? (optional)',
      notePlaceholder: 'Anything else? (optional)',
      success: 'Your subscription will cancel at the end of the period.',
      reasons: {
        too_expensive: 'Too expensive',
        missing_features: 'Missing features',
        switching: 'Switching to another product',
        no_longer_needed: 'No longer needed',
        other: 'Other',
      },
    },
    reactivate: {
      button: 'Reactivate subscription',
      success: 'Your subscription has been reactivated.',
    },
    balance: {
      title: 'Credit balance',
      credits: 'credits',
      reserved: '{{count}} reserved',
    },
    seats: {
      title: 'Seats',
      save: 'Save seats',
      admin: 'Admin',
      publisher: 'Publisher',
      viewer: 'Viewer',
    },
    topup: {
      title: 'Top up credits',
      blockCount: 'Blocks',
      blockHelper: '= {{credits}} credits',
      buy: 'Top up',
      charged: 'Credits added successfully.',
    },
    portal: {
      button: 'Manage billing',
    },
    empty: {
      title: 'No active subscription',
      body: 'Your organization does not have an active subscription yet.',
    },
    outOfCredits: {
      title: 'Out of credits',
      body: 'This action needs more credits than your organization currently has available.',
      shortfall: 'Shortfall',
      balance: 'Current balance',
      askAdmin: 'Ask your organization admin to top up credits.',
      dismiss: 'Dismiss',
      topUp: 'Top up',
    },
  },
  signup: {
    title: 'Create your workspace',
    subtitle: 'Start your 14-day free trial.',
    orgName: 'Organization name',
    subdomain: 'Workspace address',
    email: 'Admin email',
    seats: 'Seats',
    seatsAdmin: 'Admin',
    seatsPublisher: 'Publisher',
    seatsViewer: 'Viewer',
    planSummary: 'Per-seat plan · {{cadence}}',
    cadenceMonthly: 'Monthly',
    cadenceAnnual: 'Annual',
    submit: 'Continue to checkout',
    submitting: 'Starting checkout…',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    available: 'Available',
    checking: 'Checking…',
    errors: {
      subdomainFormat: 'Use lowercase letters, numbers and hyphens (3–63 chars)',
      subdomainReserved: 'That address is reserved — pick another',
      subdomainTaken: 'That address is not available — pick another',
      emailTaken: 'That email cannot be used for a new signup',
      startFailed: 'Could not start checkout. Please try again.',
    },
    success: {
      provisioning: 'Setting up your workspace…',
      provisioningHint: 'This usually takes under a minute.',
      readyTitle: 'Your workspace is ready',
      readyBody: 'We emailed a sign-in invite to {{email}}. Open it to set your password.',
      goToLogin: 'Go to sign in',
      failedTitle: 'Something went wrong',
      failedBody: 'We could not finish setting up your workspace. Please contact support.',
      timeoutBody: 'Still finishing up — check your email for the invite, then sign in.',
    },
  },
  groups: {
    title: 'Groups',
    subtitle: 'Manage user groups and sharing',
    create: 'Create Group',
    name: 'Name',
    description: 'Description',
    members: 'Members',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    deleteGroup: 'Delete group',
    deleteConfirm: 'Delete this group? Resources shared with it will no longer be accessible to its members.',
    save: 'Save',
    cancel: 'Cancel',
    noGroupsTitle: 'No groups yet',
    noGroupsBody:
      'Once you create a group, share resources with it via the Share button on any map, dashboard, or layer.',
    addMember: 'Search users by name or email...',
    removeMember: 'Remove member',
    nameRequired: 'Name is required',
    myGroupsTitle: 'My Groups',
    myGroupsHelp: 'Groups you belong to. Contact your admin to join or leave a group.',
    myGroupsEmpty: "You're not a member of any group yet.",
  },
  forms: {
    title: 'Forms',
    subtitle: 'Build forms that collect data straight into your layers',
    newForm: 'New form',
    empty: 'No forms yet',
    emptyBody: 'Create your first form to start collecting field data.',
    create: 'Create form',
    creating: 'Creating…',
    statusDraft: 'Draft',
    statusPublished: 'Published',
    statusArchived: 'Archived',
    archive: 'Archive',
    archiveConfirm: 'Archive this form?',
    archiving: 'Archiving…',
    unarchive: 'Unarchive',
    filterActive: 'Active',
    filterArchived: 'Archived',
    searchPlaceholder: 'Search forms',
    sortLabel: 'Sort',
    sortUpdated: 'Recently updated',
    sortName: 'Name',
    sortStatus: 'Status',
    noMatchTitle: 'No forms match',
    noMatchBody: 'Try a different search.',
    meta: {
      updated: 'Updated {{time}}',
      fieldCount_one: '{{count}} field',
      fieldCount_other: '{{count}} fields',
      targetNew: 'New layer',
      targetExisting: 'Existing layer',
    },
    loadError: 'Failed to load forms',
    actionError: 'Action failed',
    archivedEmptyTitle: 'No archived forms',
    archivedEmptyBody: 'Forms you archive will appear here.',
    nameLabel: 'Title',
    edit: 'Edit',
    common: {
      yes: 'Yes',
      no: 'No',
    },
    submissions: {
      openOnMap: 'Open on map',
      // A response whose map row a layer replace swapped away (`targetReplaced` from list/get).
      targetReplaced: 'Map row replaced',
      targetReplacedHint:
        "The layer's data was replaced after this response was saved, so its row on the map is gone. The response itself is kept.",
      backToForms: 'Back to forms',
      title: 'Submissions',
      viewResponses: 'Responses',
      subtitle: 'Responses collected by this form',
      empty: 'No submissions yet',
      emptyBody: 'Responses will appear here once people start filling in this form.',
      loadError: 'Could not load submissions',
      loadMore: 'Load more',
      count_one: '{{count}} response',
      count_other: '{{count}} responses',
      filterStatus: 'Status',
      filterAll: 'All',
      noMatch: 'No submissions match this filter.',
      anonymous: 'Anonymous',
      unknownUser: 'Unknown user',
      deletedUser: '(deleted)',
      view: 'View',
      delete: 'Delete',
      retry: 'Retry',
      col: {
        submitted: 'Submitted',
        by: 'By',
        featureId: 'Feature ID',
        action: 'Action',
        status: 'Status',
        actions: 'Actions',
      },
      action: {
        create: 'Created',
        update: 'Edited',
        delete: 'Deleted',
      },
      statusLabel: {
        committed: 'Committed',
        pending: 'Pending',
        failed: 'Failed',
        orphaned: 'Orphaned',
      },
      detail: {
        title: 'Submission',
        close: 'Close',
        fields: 'Fields',
        geometry: 'Geometry',
        noGeometry: 'No geometry',
        rawPayload: 'Raw payload',
        download: 'Download',
        noFile: 'No file',
        targetError: 'Why it was not saved to the layer',
      },
      deleteConfirm: {
        title: 'Delete submission?',
        body: 'This also removes the feature from the target layer. This cannot be undone.',
        bodyReplaced:
          "This removes only the response. Its map row was already replaced when the layer's data was replaced, so nothing on the map changes. This cannot be undone.",
        confirm: 'Delete',
        cancel: 'Cancel',
        error: 'Could not delete the submission',
      },
      retryDialog: {
        title: 'Retry saving this response?',
        bodyRejected:
          'The layer refused this response when it was sent. Its saved answers will be written to the layer again, checked against the form as it is now.',
        warningUnknown:
          'This response may already be in the layer: the earlier attempt never confirmed whether it saved it. Retrying may create a duplicate feature, and a duplicate cannot be found and removed automatically. Check the layer first.',
        confirm: 'Retry',
        confirmAnyway: 'Retry anyway',
        cancel: 'Cancel',
        error: 'Could not retry the response',
        definitionChanged: 'The form has changed since this response was sent, so its saved answers no longer pass:',
        definitionChangedHint: 'Change the form so these answers fit, or delete this response.',
        attachmentMissing: 'A photo or file this response uploaded is no longer stored, so it cannot be written again.',
        refusedAgain: 'The layer refused this response again. Open the response to see why.',
        outcomeUnknown:
          'The earlier attempt for this response can no longer be confirmed either way. Read the warning before you retry.',
      },
      retryNotice: {
        committed: 'The response was saved to the layer.',
        pending: 'The layer did not confirm the write. It may have been saved: check the layer before you retry again.',
      },
      edit: {
        title: 'Edit submission',
        loading: 'Loading the current feature…',
        loadError: 'Could not load the current feature',
        cancel: 'Cancel',
        retry: 'Retry',
        conflictTitle: 'This submission changed',
        conflictBody:
          'Someone else edited this feature since you opened it. Reload to get the latest, then re-apply your changes.',
        reload: 'Reload',
        save: 'Save changes',
        saving: 'Saving…',
      },
    },
    builder: {
      backToForms: 'Forms',
      changedElsewhere:
        'This form was changed elsewhere. Your unsaved edits are kept; reload to see the other changes.',
      reload: 'Reload',
      reloadConfirm: 'Discard your unsaved edits and load the changed form?',
      save: 'Save',
      saving: 'Saving…',
      saved: 'Saved',
      loadError: 'Failed to load form',
      tabEdit: 'Edit',
      tabPreview: 'Preview',
      panePalette: 'Fields',
      paneCanvas: 'Form',
      paneSettings: 'Settings',
      blockingBanner: 'Fix {{count}} issue(s) before saving',
      emptyCanvas: 'Add a field from the palette to start building',
      previewSubmit: 'Submit (preview only)',
      addField: 'Add field',
      deleteField: 'Delete field',
      moveFieldUp: 'Move {{label}} up',
      moveFieldDown: 'Move {{label}} down',
      fieldMoved: '{{label}} moved to position {{position}} of {{total}}',
      nameRequired: 'Give the form a title before saving or publishing',
      saveFailed: 'Your changes were not saved. Your edits are still here.',
      saveFailedNotPublished: 'Your changes were not saved, so the form was not published. Your edits are still here.',
      saveErrorGeneric: 'The server gave no reason. Check your connection and try again.',
      columnsAdded_one: '{{count}} column added to the layer',
      columnsAdded_other: '{{count}} columns added to the layer',
    },
    palette: {
      heading: 'Fields',
      text: 'Short text',
      textarea: 'Long text',
      integer: 'Integer',
      decimal: 'Decimal',
      boolean: 'Yes / No',
      date: 'Date',
      selectOne: 'Single select',
      selectMultiple: 'Multi select',
      photo: 'Photo',
      file: 'File',
      location: 'Location',
    },
    config: {
      heading: 'Field settings',
      label: 'Label',
      autoLabel: 'Using auto-generated label: «{{label}}»',
      autoLabelTag: 'Auto',
      name: 'Field name',
      nameHint: 'Lowercase letters, digits and underscores; must start with a letter',
      lockedAfterPublish: 'Locked after publishing',
      reservedColumnName: "The layer uses '{{name}}' for a column of its own. Give this field another name.",
      required: 'Required',
      booleanAlwaysAnswered: 'A Yes / No field always has an answer: left untouched, it counts as No.',
      helpText: 'Help text',
      placeholder: 'Placeholder',
      defaultValue: 'Default value',
      geometryType: 'Geometry type',
      geometryPoint: 'Point',
      geometryLine: 'Line',
      geometryPolygon: 'Polygon',
      geometryLockedTo: 'Locked to the target layer ({{geometry}})',
      mapsToColumn: 'Maps to column',
      mapsToColumnPlaceholder: 'Pick a column…',
      mapsToColumnHint: 'The target-layer column this field writes to.',
      mapsToColumnLoading: 'Loading columns…',
      mapsToColumnError: "Couldn't load the layer schema.",
      mapsToColumnNone: 'No compatible column on the target layer for this field type.',
      notNull: 'NOT NULL',
      options: 'Options',
      addOption: 'Add option',
      optionValue: 'Value',
      optionLabel: 'Label',
      validationHeading: 'Validation',
      min: 'Min',
      max: 'Max',
      minLength: 'Min length',
      maxLength: 'Max length',
      pattern: 'Pattern (regex)',
      logicHeading: 'Conditional logic',
      addRule: 'Add rule',
      ruleAction: 'Action',
      actionShow: 'Show this field',
      actionHide: 'Hide this field',
      actionRequire: 'Require this field',
      ruleMatch: 'when',
      matchAll: 'all of',
      matchAny: 'any of',
      addCondition: 'Add condition',
      conditionField: 'Field',
      conditionOp: 'Condition',
      conditionValue: 'Value',
      removeOption: 'Remove option',
      removeRule: 'Remove rule',
      removeCondition: 'Remove condition',
      pickValue: 'Pick a value…',
      neverMatches: {
        boolean: 'This value never matches. Pick Yes or No.',
        option: 'This value never matches. Pick one of the options.',
        number: 'This value never matches. Enter a number.',
        date: 'This value never matches. Pick a date.',
      },
      ruleMalformed: 'This rule is malformed, so it may not work as shown. Edit it or remove it.',
      rulesMalformed: "This field's rules are malformed and are ignored. Adding a rule replaces them.",
    },
    settings: {
      heading: 'Form settings',
      name: 'Title',
      description: 'Description',
      sharingOwner: 'Only me',
      sharingOrganization: 'My organization',
      sharingGroups: 'Specific groups',
      sharingPublic: 'Public',
    },
    op: {
      eq: 'equals',
      neq: 'does not equal',
      gt: 'greater than',
      lt: 'less than',
      gte: 'at least',
      lte: 'at most',
      contains: 'contains',
      empty: 'is empty',
      not_empty: 'is not empty',
    },
    fill: {
      submit: 'Submit',
      submitting: 'Submitting…',
      loading: 'Loading…',
      loadError: 'This form is not available.',
      requiredLegend: 'indicates a required field',
      successTitle: 'Thank you',
      successDefault: 'Your response has been recorded.',
      submitAnother: 'Submit another response',
      anonymousNotice: 'You can submit this form without signing in.',
      submittingAs: 'Submitting as {{name}}',
      poweredBy: 'Powered by Gisify',
      privacyNote: 'Your responses are sent to the form owner.',
      networkError: 'Could not submit — please check your connection and try again.',
      submitFailed: 'Your response could not be saved. Please try again later, or contact the form owner.',
      submitPending: 'We could not confirm that your response was saved. Please try again in a moment.',
      signInRequired: 'This form requires sign-in to submit. Please sign in to continue.',
      signInToOpen: 'Sign in to open this form',
      notAccepting: "This form isn't accepting responses right now.",
      signIn: 'Sign in',
      invalidSummary: '{{count}} field(s) need attention',
      validation: {
        wholeNumber: '{{label}} must be a whole number',
        number: '{{label}} must be a number',
        required: '{{label}} is required',
        atLeast: '{{label}} must be at least {{limit}}',
        atMost: '{{label}} must be at most {{limit}}',
        minLength_one: '{{label}} must be at least {{count}} character',
        minLength_other: '{{label}} must be at least {{count}} characters',
        maxLength_one: '{{label}} must be at most {{count}} character',
        maxLength_other: '{{label}} must be at most {{count}} characters',
        pattern: '{{label}} does not match the required format',
        outOfRange: '{{label}} is out of the allowed range',
        yesNo: '{{label}} must be Yes or No',
        invalidOption: '{{label}} has an answer that is not one of its options',
        geometryPoint: '{{label}} must be a point',
        geometryLine: '{{label}} must be a line',
        geometryPolygon: '{{label}} must be an area',
        geometryBounds: '{{label}} is outside the valid range (longitude -180 to 180, latitude -90 to 90)',
      },
      draft: {
        notice: 'You have an unsaved draft of this form. Restore it to continue where you left off.',
        restore: 'Restore draft',
        discard: 'Discard draft',
      },
      capture: {
        empty: 'No location set',
        setLocation: 'Set on map',
        editLocation: 'Edit on map',
        clear: 'Clear',
        done: 'Done',
        cancel: 'Cancel',
        close: 'Close',
        redraw: 'Redraw',
        useMyLocation: 'Use my location',
        accuracy: '±{{meters}} m',
        gpsError: 'Couldn’t get your location. Try again or set it on the map.',
        pointSet: 'Point set',
        lineSet_one: 'Line · {{count}} vertex',
        lineSet_other: 'Line · {{count}} vertices',
        polygonSet_one: 'Area · {{count}} vertex',
        polygonSet_other: 'Area · {{count}} vertices',
        instructionsPoint: 'Tap the map to place a point.',
        instructionsLine: 'Tap to add points; double-click to finish the line.',
        instructionsPolygon: 'Tap to add points; double-click to close the area.',
      },
      upload: {
        choose: 'Choose file',
        replace: 'Replace',
        remove: 'Remove',
        uploading: 'Uploading…',
        progress: '{{percent}}%',
        tooLarge: 'File is too large (max 25 MB)',
        notAllowed: 'This file type is not allowed',
        failed: 'Upload failed — try again',
        noFile: 'No file',
        attachedFile: 'Attached file',
        attachedFileWithType: 'Attached file ({{ext}})',
      },
    },
    collect: {
      title: 'Forms',
      loading: 'Loading forms…',
      error: 'Could not load forms.',
      empty: 'No data-collection forms target the layers on this map.',
      submit: 'Submit',
      successTitle: 'Response recorded',
      successBody: 'The feature was added to the layer.',
      fillAnother: 'Fill another',
      backToList: 'Back to forms',
    },
    target: {
      heading: 'Publish target',
      engine: 'Data source',
      server: 'Connection',
      noServers: 'No connections found. Add one under Settings → Servers.',
      managedDatabase: 'Gisify cloud database',
      managedHint: "This form's layer will be created in your managed Gisify cloud database.",
      mode: 'Where do submissions go?',
      newTable: 'Create a new layer',
      existingLayer: 'Use an existing layer',
      layer: 'Layer',
      noLayers: 'No layers found for this connection.',
      geometryLocked: 'Geometry type follows the target layer.',
      lockedHint: "The publish target can't be changed after publishing.",
      editableLayersOnly: 'Only layers you can edit are listed.',
      layerWriteForbidden: 'You need edit access to this layer to collect form responses into it.',
      layerNotEditable: 'Editing is turned off for this layer. Ask an admin to turn it on, or pick another layer.',
      columnMapping: 'Map fields to columns',
      mapColumnFor: 'Column for "{{field}}"',
      auditColumns: 'Add giscenter_* attribution columns to the layer',
      auditColumnsHint: 'Records who submitted each feature, inside your GIS layer.',
      engineTag: {
        postgresql: 'PostgreSQL',
        arcgis_online: 'ArcGIS Online',
        arcgis_enterprise: 'ArcGIS Enterprise',
        geoserver: 'GeoServer',
      },
      geoserverExistingOnly: 'GeoServer forms submit to an existing layer.',
      schemaLoading: 'Loading layer columns…',
      schemaError: "Couldn't load the layer schema.",
      schemaReady: '{{count}} column available on the target layer',
      schemaReady_other: '{{count}} columns available on the target layer',
      save: 'Save target',
      cancel: 'Cancel',
    },
    publish: {
      publish: 'Publish',
      republish: 'Re-publish',
      publishing: 'Publishing…',
      published: 'Published',
      setTargetFirst: 'Choose a publish target first',
      confirmAuditTitle: 'Add attribution columns?',
      confirmAuditBody: 'This will add {{count}} giscenter_* column(s) to the target layer.',
      confirm: 'Add columns and publish',
      openForm: 'Open form',
      copyLink: 'Copy link',
      copied: 'Copied',
      publishError: 'Publish failed',
    },
    share: {
      title: 'Share form',
      tabLink: 'Link',
      tabQr: 'QR',
      tabEmbed: 'Embed',
      tabAccess: 'Access',
      linkLabel: 'Form link',
      copy: 'Copy',
      copied: 'Copied',
      qrHint: 'Scan to open the form on a phone.',
      qrDownload: 'Download QR',
      embedLabel: 'Embed code',
      embedHint: 'Paste this into a web page to embed the form.',
      embedPublicOnly: "Embedded forms only work for public forms. Visitors can't sign in inside an embed.",
      // Who the link lets in, per sharing level (#1480): shown beside the builder's Open form
      // link and on the Link, QR and Embed tabs.
      accessHint: {
        owner: 'Only you can open this link',
        organization: 'Signed-in members of your organization',
        groups: 'Members of the selected groups',
        public: 'Anyone with the link',
      },
      changeAccess: 'Change access',
      accessLevel: 'Who can access',
      allowAnonymous: 'Allow anonymous submissions',
      anonymousHint: 'Only public forms can accept anonymous submissions.',
      save: 'Save',
      saving: 'Saving…',
      saveError: 'Could not update sharing',
      groupsLabel: 'Share with groups',
      groupsEmpty: 'No groups available. Create groups in Settings first.',
      groupsRequired: 'Select at least one group.',
    },
  },
  alerts: {
    // Flat keys for the robot_alerts dashboard element (Slice 6 — palette +
    // config drawer scope/severity form). The nested keys below are Slice 5's
    // (header bell + inbox drawer); `empty` stays the {title,body} object.
    // The full-screen emergency modal (#1272). Its own sub-block because these are the only strings
    // in the console that an operator must understand instantly, without translation fallback.
    sos: {
      title: 'Emergency',
      missedCount_one: '{{count}} missed emergency',
      missedCount_other: '{{count}} missed emergencies',
      missedAgo: 'Missed · {{when}}',
      missedHint: 'Nobody answered these in time. They are still open — call back or dismiss each one.',
      callBack: 'Call back',
      dismiss: 'Dismiss',
      handledElsewhere: '{{label}} was answered elsewhere.',
      callBusy: 'No call was placed — another emergency call is already in progress.',
      unknownDevice: 'Unknown device',
      waiting_one: '{{count}} more emergency waiting',
      waiting_other: '{{count}} more emergencies waiting',
      accept: 'Accept',
      acceptAndCall: 'Accept & call',
      decline: 'Decline',
      failed: "Couldn't record your answer. Try again.",
      call: {
        title: 'Emergency call',
        with: 'Calling {{name}}',
        dismiss: 'Dismiss',
        starting: 'Getting your microphone ready…',
        ringing: 'Ringing the helmet…',
        connected: 'Connected',
        idle: '',
        mute: 'Mute',
        unmute: 'Unmute',
        muted: 'Your microphone is off',
        callAction: 'Call',
        callName: 'Call {{name}}',
        unanswered: 'This helmet did not answer the last call.',
        retryUnanswered: 'This helmet did not answer the last call. Call {{name}} again?',
        busy: 'Another call is already in progress.',
        micDevice: 'Microphone',
        micDefault: 'System default',
        settings: 'Call settings',
        micLevel: 'Level',
        micGain: 'Microphone boost',
        micSilent:
          'Your microphone is sending silence — the wearer cannot hear you. Check the input device in your system settings.',
        hangUp: 'Hang up',
        ended: {
          hung_up: 'Call ended.',
          idle: 'The call timed out.',
          max: 'The call reached its time limit.',
        },
        failed: {
          unanswered: 'The helmet did not answer. It may not support calls.',
          mic_denied: 'Microphone access was blocked. Allow it and try again.',
          mic_failed: 'Your microphone could not be opened.',
          mic_unavailable: 'No microphone is available.',
          no_broker_ack: 'The call was never acknowledged by the server. It may not be set up for calls.',
          offline: 'This helmet is not connected.',
          channel_lost: 'Lost the connection to the helmet.',
          connection_lost: 'The call dropped.',
          publish_failed: 'Your microphone could not reach the call.',
          media_failed: "Could not receive the wearer's audio.",
          media_timeout: "The wearer's audio never arrived. They may not be able to hear you either.",
          call_gone: 'The call has already ended.',
          calls_unavailable: 'Calling is not available on this deployment.',
          calls_not_configured: 'Calling is not set up on this deployment yet. Contact your administrator.',
          device_unidentified: 'The helmet has not finished connecting. Try again in a moment.',
          not_prepared: 'The call was not set up. Try again.',
          forbidden_role: 'Your account cannot place calls. Ask an administrator.',
          rejected: 'The call was refused.',
          failed: 'The call could not be placed.',
        },
      },
    },
    log: {
      summary: '{{open}} open · {{entries}} entries',
      pictures_one: '{{count}} picture',
      pictures_other: '{{count}} pictures',
      pictureTaken: 'Picture taken',
      pictureTimeHint: 'Time the picture reached the server. The device does not report when the shutter fired.',
      viewPicture: 'View picture',
      pictureUnavailable: "Couldn't load this picture",
      timedOut: 'No longer reporting',
      missed: 'Missed',
      timedOutHint:
        'This alarm has not been reported for over five minutes, so it no longer interrupts. It is still open.',
      repeatsSince: '{{count}}× since {{time}}',
      showRepeats: 'Show all',
      hideRepeats: 'Hide repeats',
      earlier_one: '{{count}} earlier occurrence not listed',
      earlier_other: '{{count}} earlier occurrences not listed',
      showOnMap: 'Show on map',
      noLocation: 'No location',
      locationOfLatest: 'Location of the latest occurrence of this alarm',
      helmet: 'Helmet',
      tracker: 'Tracker',
      truncated: 'Showing the newest {{shown}} of {{total}} entries',
      serverCap: 'The server keeps the newest {{count}} alarms and pictures; older ones are not listed.',
      loadFailed: "Couldn't load the alert history.",
      retry: 'Retry',
    },
    status: {
      acknowledged: 'Acknowledged',
      resolved: 'Resolved',
    },
    paletteLabel: 'Robot Alerts',
    paletteDescription: 'Open robot alerts inbox',
    scopeFleet: 'Whole fleet',
    scopeRobot: 'Single robot',
    ack: 'Acknowledge',
    resolve: 'Resolve',
    filtered: 'filtered',
    empty: {
      title: 'No alerts yet',
      body: 'Alarms and pictures will appear here as they arrive.',
    },
    actions: {
      enroll: 'Enroll',
      clearAll: 'Clear map pins',
    },
    clearAll: {
      confirmTitle: 'Clear all map pins?',
      confirmBody:
        'Resolve every open alert in this scope so its map pin is removed — including any not listed here. Nothing is removed from this log.',
      confirmCta: 'Clear pins',
      cancel: 'Cancel',
    },
    errors: {
      clearAllFailed: "Couldn't clear the alerts. Please try again.",
    },
    bell: {
      open: 'Open alerts',
    },
    drawer: {
      title: 'Robot alerts',
      close: 'Close',
    },
  },
  deviceList: {
    loading: 'Loading devices…',
    selectDevice: 'Select device',
    tick: 'Tick',
    tickGroup: 'Tick every device in',
    ticked: '{{count}} ticked',
    clearTicks: 'Clear',
    otherDevices: 'Other devices',
    unnamedGroup: 'Unnamed group',
    groupEmpty: 'No available devices in this group',
    empty: {
      title: 'No devices',
      body: 'Devices registered to your fleet will appear here.',
    },
    emptyChosen: {
      title: 'None of the chosen devices are available',
      body: 'Pick the devices to show in this element’s Settings.',
    },
  },
  helmetWall: {
    lightOn: 'Light on',
    lightOff: 'Light off',
    torchHint: "Turn the helmet's light on or off",
    laserOn: 'Laser on',
    laserOff: 'Laser off',
    laserHint: "Turn the helmet's laser pointer on or off",
    dblclickFocus: 'Double-click to focus',
    dblclickGrid: 'Double-click to return to the grid',
    fromList: 'Ticked in the device list',
    photo: 'Photo',
    record: 'Record',
    stopRecording: 'Stop rec',
    focus: 'Focus',
    unfocus: 'Grid',
    stopLive: 'Stop live',
    startLive: 'Start live',
    removeHint: 'Take this helmet off the wall',
    paused: 'Live view stopped. Press Start live to watch again.',
    mbps: '{{mbps}} Mbit/s',
    bulk: {
      selectAll: 'Select all',
      selected: '{{count}} selected',
      photo: 'Take photo',
      recordStart: 'Start rec',
      recordStop: 'Stop rec',
      liveStart: 'Start live',
      liveStop: 'Stop live',
      clear: 'Clear',
      result: {
        queued: 'Queued',
        sent: 'Sent',
        photo: 'Photo received',
        recording: 'Recording',
        stopped: 'Stopped',
        live: 'Live',
        unconfirmed: 'Unconfirmed',
        offline: 'Offline',
        refused: 'Refused ({{code}})',
        notSent: 'Not sent',
      },
    },
    selectTile: 'Select {{name}}',
    missing: 'This helmet is no longer registered.',
    offline: 'Offline — the video starts when the helmet reconnects.',
    preview: 'Live video is paused in the builder.',
    onCall: 'On a call — the camera returns when it ends.',
    privacy: 'Privacy mode — the wearer has paused the camera.',
    count: '{{online}} of {{shown}} online',
    layout: 'Layout',
    add: 'Add helmet',
    noHelmets: 'No helmets are registered for this organisation.',
    empty: 'No helmets on this wall. Use Add helmet to bring one back.',
    badge: {
      fall: 'Fall',
      electric: 'Electric',
      alarm: 'Alarm',
      unanswered: 'Unanswered — acknowledge or resolve it in the alert list',
      sos: 'SOS',
      worn: 'Worn',
      notWorn: 'Not worn',
      rec: 'Rec',
      onCall: 'On call',
      privacy: 'Privacy',
    },
  },
  tracker: {
    register: {
      open: 'Register tracker',
      title: 'Register tracker',
      close: 'Close',
      name: 'Name',
      imei: 'IMEI',
      server: 'PostGIS server',
      selectServer: 'Select a server…',
      model: 'Model (optional)',
      sendPeriod: 'Send period (seconds)',
      cancel: 'Cancel',
      submit: 'Register',
      submitting: 'Registering…',
      success: 'Tracker registered: {{name}}',
      settingsIntro:
        'In Teltonika Configurator, open GPRS → Server settings, enter these values and save to the device:',
      done: 'Done',
      errors: {
        nameRequired: 'Enter a name for this tracker.',
        imeiInvalid: 'The IMEI must be exactly 15 digits.',
        serverRequired: 'Pick the PostGIS server that stores positions.',
        sendPeriodInvalid: 'Send period must be a whole number of seconds from 10 to 86400.',
        failed: 'Registration failed. Please try again.',
      },
      settings: {
        domain: 'Server domain',
        port: 'Port',
        protocol: 'Protocol',
        dataProtocol: 'Data protocol',
        serverConfirmation: 'Server confirmation',
        unknownHost: 'Not configured on this deployment',
      },
    },
    status: {
      recordedAt: 'Last record',
      receivedAt: 'Received',
      presenceLabel: 'Presence',
      presence: {
        online: 'Online',
        stale: 'Stale',
        offline: 'Offline',
      },
      lastSeen: 'Last seen',
      battery: 'Battery',
      charging: 'Charging',
      yes: 'Yes',
      no: 'No',
      batteryMv: 'Battery voltage',
      gnssStatus: 'GNSS',
      gnss: {
        off: 'Off',
        fix: 'Fix',
        noFix: 'No fix',
        sleep: 'Sleep',
      },
      satellites: 'Satellites',
      hdop: 'HDOP',
      speed: 'Speed',
      heading: 'Heading',
      movement: 'Movement',
      moving: 'Moving',
      stationary: 'Stationary',
      gsmSignal: 'GSM signal',
      temperature: 'Temperature',
      operator: 'Operator',
      sections: {
        status: 'Status',
        power: 'Power',
        gnss: 'GNSS',
        device: 'Device',
      },
      empty: 'No report from this tracker since the page opened. It appears with the next record the device sends.',
      selectPrompt: 'Select a tracker on this dashboard to see its status.',
      pickPrompt: 'Pick a tracker in this element’s settings.',
    },
    track: {
      title: 'Track',
      start: 'Start',
      end: 'End',
      clear: 'Clear',
      loading: 'Loading track…',
      error: "Couldn't load the track.",
      empty: 'No positions in this period.',
      points: '{{count}} points',
      truncated: '{{count}} most recent points (limit reached — older ones not shown)',
      preset: {
        '1h': '1h',
        '6h': '6h',
        '24h': '24h',
        '7d': '7d',
      },
    },
    config: {
      title: 'Tracker status',
      tracker: 'Tracker',
      select: 'Select a tracker…',
      missing: 'The picked tracker is not in the fleet roster (removed, or still loading).',
      follow: 'Follow robot selection',
      followHint:
        'On a fleet dashboard, selecting a device shows that device instead of the tracker picked above. When off, this tile always shows the tracker above.',
    },
  },
  geocoding: {
    title: 'Geocoding',
    steps: { source: 'Select Source', fields: 'Map Fields', options: 'Options', review: 'Review' },
    providers: { nominatim: 'Nominatim (OpenStreetMap)', arcgis: 'ArcGIS World Geocoding' },
    status: { processing: 'Processing', completed: 'Completed', failed: 'Failed' },
    wizard: {
      uploadPrompt: 'Click or drag file to upload',
      uploadHint: 'Supported formats: CSV, TXT, Excel (.xlsx)',
      reading: 'Uploading and reading file…',
      fileLoaded: 'File loaded: {{rows}} rows, {{columns}} columns',
      fileLoadedUnknownRows: 'File loaded: {{columns}} columns (too large to count rows up front)',
      fileLoadedEstimatedRows: 'File loaded: about {{rows}} rows (estimated), {{columns}} columns',
      rowsEstimated: 'About {{rows}} rows (estimated)',
      rowsUnknown: 'Not counted',
      stop: 'Stop',
      stopping: 'Stopping…',
      stopFailed: 'Failed to stop the run',
      stoppedTitle: 'Stopped at {{processed}} of {{total}}',
      stoppedBody: 'The rows done so far are kept. Unused credits were refunded.',
      resume: 'Resume',
      exportPartial: 'Export partial results',
      delete: 'Delete',
      deleteTitle: 'Delete this run?',
      deleteBody: 'This deletes the run and the rows it geocoded so far. This cannot be undone.',
      deleteFailed: 'Failed to delete the run',
      resumeUnsupported:
        "This run was started before stop and resume existed, so it can't be resumed. Export what it finished, or start it again.",
      resumeNoCredits: 'Not enough credits to resume this run. Top up, then try again.',
      resumeFailed: 'Failed to resume the run',
      addressFormat: 'Address Format',
      modeConcatenate: 'Simple Concatenation',
      modeStructured: 'Structured Query',
      selectColumns: 'Select columns to combine (in order)',
      selectColumnsHelp: 'Columns will be joined with commas to form the address',
      addColumn: 'Add',
      moveUp: 'Move {{column}} up',
      moveDown: 'Move {{column}} down',
      removeColumn: 'Remove {{column}}',
      suffixLabel: 'Address Suffix (optional)',
      suffixHelp: "Added to every address, e.g., 'Malaysia' or 'Kedah, Malaysia'",
      suffixPlaceholder: 'e.g., Malaysia',
      addressPreview: 'Address Preview',
      noPreview: 'No preview available',
      expectedRegionLabel: 'Expected Region/State (optional)',
      expectedRegionHelp: 'Results from other regions will be marked as failed.',
      expectedRegionPlaceholder: 'e.g., Kedah, Selangor, Johor',
      previewFailed: 'Could not read the uploaded file.',
      previewUnavailable:
        "We could not read this file's columns, so there is nothing to map yet. Your file is safely uploaded — try again.",
      retryPreview: 'Read the file again',
      legacyXlsUnsupported:
        'Legacy Excel files (.xls) are not supported. Open the file in Excel and save it as .xlsx or CSV, then upload it again.',
    },
    regeocode: {
      inheritedGatesHelp:
        'Carried over from the previous pass, along with its boundary if it had one. Clear this field to drop the region check for this pass.',
    },
  },
  digitize: {
    edit: {
      reshape: 'Reshape geometry',
      undo: 'Undo',
      redo: 'Redo',
      delete: 'Delete',
      save: 'Save',
      reload: 'Reload',
      noFields: 'No editable fields.',
      selectLayer: 'Select an editable layer to begin editing.',
      clickFeature: 'Click a feature to edit it.',
      conflict: 'This feature was modified by someone else. Reload to get the latest version before saving.',
      loadFailed: 'Failed to load the feature.',
      openFailed: 'Could not open this editable layer. It may have been deleted, or you are offline.',
      saveFailed: 'Failed to save the feature.',
      deleteFailed: 'Failed to delete the feature.',
    },
    create: {
      title: 'New editable layer',
      namePlaceholder: 'Layer name',
      descriptionPlaceholder: 'Description (optional)',
      geometryType: 'Geometry type',
      geometryPoint: 'Point',
      geometryLine: 'Line',
      geometryPolygon: 'Polygon',
      fields: 'Fields',
      fieldNamePlaceholder: 'field_name',
      fieldType: 'Field type',
      requiredShort: 'req',
      requiredAria: 'Required field',
      removeField: 'Remove field',
      addField: 'Add field',
      managedHint: 'This layer will be created in your managed Gisify cloud database.',
      targetServer: 'Target server',
      selectServer: 'Select a server…',
      serverHint: 'New layers can only be created on PostgreSQL or ArcGIS servers.',
      cancel: 'Cancel',
      createButton: 'Create layer',
      nameRequired: 'Name is required',
      createFailed: 'Failed to create layer. Please try again.',
      duplicateFields: 'Field names must be unique.',
      reservedFields: 'These field names are reserved and cannot be used: {{names}}',
      close: 'Close',
      newFeature: 'New feature',
      saveFeature: 'Save feature',
    },
    schema: {
      title: 'Edit schema',
      editSchema: 'Edit schema',
      addField: 'Add field',
      saveSchema: 'Save schema',
      saving: 'Saving…',
      cancel: 'Cancel',
      unsupportedTarget: 'Schema editing is only supported on PostgreSQL and ArcGIS layers.',
      notEditable: 'This layer is not an editable digitization layer.',
      uniqueNames: 'Field names must be unique.',
      reusedRemovedName: 'A removed field name cannot be reused in the same edit. Rename instead.',
      conflictReload: 'This feature was modified by someone else. Reload and try again.',
    },
  },
  library: {
    editing: {
      heading: 'Editing',
      features: 'Edit features',
      schema: 'Edit schema',
      on: 'On',
      off: 'Off',
      freezeNote: 'Enabling editing pins this layer to its current server and table until editing is turned off.',
      adminOnly: 'Only an administrator can change these settings.',
      saveError: 'Could not change the editing settings',
      auditColumns: {
        note: 'This table has no attribution columns, so edits are recorded only in the audit log. You can add them to the table:',
        add: 'Add attribution columns',
      },
    },
    replaceConfirm: {
      titleForms: 'Replace a layer that forms collect into?',
      title: "Replace this layer's data?",
      formsIntro_one: '{{count}} form collects responses into this layer:',
      formsIntro_other: '{{count}} forms collect responses into this layer:',
      formsListLabel: 'Forms that collect into this layer',
      moreForms_one: '+{{count}} more form',
      moreForms_other: '+{{count}} more forms',
      opensInNewTab: '(opens in a new tab)',
      consequencesHeading: 'If you replace it',
      rowsDeleted:
        'Every row these forms collected is deleted and replaced by the new data. The forms still list those responses, but the rows they point to are gone.',
      missingColumn:
        'If the new data has no column a form maps a field to, that form will fail every submission until the form or the layer is fixed.',
      dataReplaced: "The layer's current data is deleted and replaced by the new data.",
      columnsHeading: 'Column changes',
      columnsAdded: 'Added: {{columns}}',
      columnsRemoved: 'Removed: {{columns}}',
      columnsRetyped: 'Type changed: {{columns}}',
      geometryChanged: 'Geometry changes from {{from}} to {{to}}',
      geometryNone: 'none',
      layersAdded: 'Layers added: {{layers}}',
      layersRemoved: 'Layers removed: {{layers}}',
      noColumnChanges: 'No column changes',
      styleHeading: 'Style',
      styleColumnsRemoved:
        "The layer's style uses columns the new data doesn't have: {{columns}}. Those style rules will stop matching.",
      featuresHeading: 'Features',
      featureCount: '{{before}} → {{after}}',
      featureCountUnknown: 'unknown',
      acknowledge: "I understand the layer's current data will be replaced",
      cancel: 'Cancel',
      confirm: 'Replace data',
      error: 'The layer could not be replaced.',
    },
    replace: {
      button: 'Replace data…',
      disabled: {
        linked: "This layer points at data Gisify didn't create.",
        typeNotReplaceable: "This kind of layer can't have its data replaced.",
        missingSource: 'This layer was published before replace was supported — re-publish it once to enable replace.',
        cloudEngine: "Replace isn't available for layers on this server in a Gisify Cloud workspace.",
      },
      title: 'Replace data: {{name}}',
      intro: 'Choose a file with the new data. The layer keeps its id, name, sharing and style; its rows are replaced.',
      accepted: 'Files this layer accepts:',
      sameFormat: 'This layer was published from a {{format}} file, so only a {{format}} file can replace its data.',
      fileTypes: {
        csv: 'CSV',
        geojson: 'GeoJSON',
        shapefile: 'Shapefile (.zip)',
        geopackage: 'GeoPackage (.gpkg)',
      },
      dropzone: 'Click to choose a file, or drag it here',
      chosen: 'Chosen file: {{name}}',
      refused: {
        gdb: "A File Geodatabase can't replace a layer's data. Export it to a GeoPackage or a shapefile first.",
        multiShapefile: 'This zip holds more than one shapefile. Replace takes a zip with exactly one shapefile.',
        wrongType: "This layer can't be replaced with that kind of file.",
        unreadable: 'The file could not be read: {{reason}}',
      },
      csvCoordinates: 'Choose the latitude and longitude columns: the layer needs a location for every row.',
      gpkgTable: 'GeoPackage table',
      gpkgTableHint: 'The table in the file that holds the new data.',
      gpkgOneTable: 'The GeoPackage must hold exactly one table: the whole file replaces the layer.',
      gpkgTables: 'GeoPackage tables (one per line)',
      gpkgTablesHint:
        'List two or more tables. A table that a layer in the group was published from replaces that layer. A new table adds a layer; a layer whose table is missing is removed.',
      gpkgTablesChooseFile: 'Choose the GeoPackage to see its tables',
      gpkgTablesReading: "Reading the file's tables…",
      gpkgTablesInFile: 'Tables in this file: {{tables}}',
      gpkgTablesNone: 'This GeoPackage holds no feature tables.',
      gpkgTablesUnreadable: "This file's tables could not be listed. Type them, one per line.",
      groupLoading: "Loading the group's layers…",
      groupLoadFailed: "The group's layers could not be loaded.",
      groupNotPostgres: 'Only a layer group stored in PostgreSQL can have its data replaced.',
      check: 'Check file',
      cancel: 'Cancel',
      close: 'Close',
      retry: 'Retry',
      checking: 'Checking your file…',
      checkingHint: 'Nothing has changed yet. Gisify is comparing the file with the layer.',
      stale: "The layer's forms or columns changed since the check. Checking again…",
      staleRechecked: 'The check was run again: review the changes below.',
      replacing: 'Replacing the data…',
      replacingHint: 'You can close this window. The replace keeps running.',
      inBackground: 'Replacing the data… This page updates when it finishes.',
      done: 'Layer updated',
      doneHint: 'The layer keeps its id, sharing and style. Its data, extent and feature count are new.',
      featuresLabel: 'Features',
      statusUnknown: 'The replace status could not be read. It may still be running; reload the layer in a minute.',
      errors: {
        linkedLayer: "This layer points at data Gisify didn't create. Replace it at its source.",
        notSupported: "This file can't replace this layer's data.",
        missingSource: 'This layer was published before replace was supported — re-publish it once to enable replace.',
        generic: 'Something went wrong. Try again.',
      },
    },
  },
  planning,
} as const
